import {CSSProperties, Fragment, ReactElement, useRef, useState, useMemo, Key, useLayoutEffect} from 'react'
import {useList} from 'react-use'
import {groupBy, isNil} from 'lodash'

import {selectTheme, ThemeState} from '../../store'
import {EmaScheduleSimulatorResult, EmaType, ITaskSchedule, ScheduleTime} from '../../shared/db'
import {ScheduleFormValueHelper, ScheduleTimeHelper} from '../../model'
import {RIF, StringifyProps, formatOrdinals} from '../../lib'
import {PinIcon} from '../../lib/svg/icon/pin'
import {Position, getRelativePosition, isPositionEqual} from '../../lib/layout_util'
import {pairwise} from '../../lib/iter_util'
import {RefreshIcon} from '../../lib/svg/icon/refresh'

// -----------------------------------------------------------------------------
// Types and Conversions
// -----------------------------------------------------------------------------
const DEFAULT_CAPSULE_WIDTH = 50
const DEFAULT_CAPSULE_HEIGHT = 30
const DEFAULT_TIMELINE_MINUTE_WIDTH = 0.8
const DEFAULT_TIMELINE_WIDTH = 800

type CalculateWidthProps = {widthMultiplier: number} & (
  | {
      startTime: ScheduleTime
      endTime: ScheduleTime
    }
  | {totalSeconds: number}
)

const calculateWidth = (props: CalculateWidthProps): number => {
  let secondsInterval: number
  if ('startTime' in props) {
    secondsInterval = ScheduleTime.getSecondsInterval(props.startTime, props.endTime)
  } else {
    secondsInterval = props.totalSeconds
  }
  return Math.ceil((secondsInterval / 60) * DEFAULT_TIMELINE_MINUTE_WIDTH * props.widthMultiplier)
}

const calculateWidthModifier = (props: {
  startTime: ScheduleTime
  endTime: ScheduleTime
  minWidthMultiplier: number
}) => {
  const {startTime, endTime, minWidthMultiplier} = props
  const totalSeconds = ScheduleTime.getSecondsInterval(startTime, endTime)
  return Math.max(DEFAULT_TIMELINE_WIDTH / calculateWidth({totalSeconds, widthMultiplier: 1}), minWidthMultiplier)
}

export interface EmaExampleGraphProps {
  schedule: Partial<ITaskSchedule> | null
  simulation: EmaScheduleSimulatorResult | null
  onClickRefresh?: () => void
}

interface BaseGraphData {
  emaType: EmaType
  startTime: ScheduleTime
  endTime: ScheduleTime
  triggers: ScheduleTime[]
}

interface SemiRandomGraphData extends BaseGraphData {
  interval: number
  intervals: ScheduleTime[]
  flexibilityWindow: number
  flexibilityWindows: [ScheduleTime, ScheduleTime][]
}

interface FullyRandomGraphData extends BaseGraphData {
  minimumInterval: number
}

type BaseGraphDataStringRepresentation = StringifyProps<BaseGraphData>
type SemiRandomGraphDataStringRepresentation = StringifyProps<
  Omit<SemiRandomGraphData, 'intervals' | 'flexibilityWindows'>
>
type FullyRandomGraphDataStringRepresentation = StringifyProps<FullyRandomGraphData & {ocurrences: string}>

const scheduleToSemiRandomGraphData = ({
  schedule,
  simulation,
}: Pick<EmaExampleGraphProps, 'schedule' | 'simulation'>): SemiRandomGraphData => {
  if (schedule && simulation) {
    if (
      !isNil(schedule.startTime) &&
      !isNil(schedule.endTime) &&
      !isNil(schedule.emaIntervalSeconds) &&
      !isNil(schedule.emaMinimumGapSeconds)
    ) {
      const startTime = ScheduleTime.parse(schedule.startTime)
      const endTime = ScheduleTime.parse(schedule.endTime)
      const interval = schedule.emaIntervalSeconds

      const startTimeSeconds = startTime.totalSeconds()
      const endTimeSeconds = endTime.totalSeconds()

      const intervals: number[] = []
      if (interval > 0) {
        let nextIntervalSeconds = startTimeSeconds + interval
        while (nextIntervalSeconds < endTimeSeconds) {
          intervals.push(nextIntervalSeconds)
          nextIntervalSeconds += interval
        }
      }

      const flexibilityWindow = ScheduleFormValueHelper.emaMinimumGapSecondsToEmaFlexibilityWindowSeconds({
        emaIntervalSeconds: interval,
        emaMinimumGapSeconds: schedule.emaMinimumGapSeconds,
      })

      const flexibilityWindows: [number, number][] = []
      if (flexibilityWindow > 0) {
        for (const [freeStart, freeEnd] of simulation.freeIntervals()) {
          let flexibilityWindowStart = freeStart
          while (flexibilityWindowStart < freeEnd) {
            const flexibilityWindowEnd = flexibilityWindowStart + flexibilityWindow
            if (flexibilityWindowEnd > freeEnd) {
              flexibilityWindows.push([flexibilityWindowStart, freeEnd])
              break
            }
            flexibilityWindows.push([flexibilityWindowStart, flexibilityWindowEnd])
            flexibilityWindowStart = flexibilityWindowEnd
          }
        }
      }

      return {
        emaType: EmaType.SemiRandom,
        startTime,
        endTime,
        interval,
        flexibilityWindow: ScheduleFormValueHelper.emaMinimumGapSecondsToEmaFlexibilityWindowSeconds({
          emaIntervalSeconds: interval,
          emaMinimumGapSeconds: schedule.emaMinimumGapSeconds,
        }),
        flexibilityWindows: flexibilityWindows.map(([start, end]) => [
          ScheduleTime.fromTotalSeconds(start),
          ScheduleTime.fromTotalSeconds(end),
        ]),
        intervals: intervals.map((seconds) => ScheduleTime.fromTotalSeconds(seconds)),
        triggers: simulation.randomizedEmaTimeIntervals().map(([start]) => start),
      }
    }
  }
  // sample data
  return {
    emaType: EmaType.SemiRandom,
    startTime: ScheduleTime.parse('14:00'),
    endTime: ScheduleTime.parse('18:00'),
    interval: 3600 * 2,
    flexibilityWindow: 3600 * 0.5,
    flexibilityWindows: [
      [ScheduleTime.parse('14:00'), ScheduleTime.parse('14:30')],
      [ScheduleTime.parse('15:30'), ScheduleTime.parse('16:00')],
      [ScheduleTime.parse('16:00'), ScheduleTime.parse('16:30')],
      [ScheduleTime.parse('17:30'), ScheduleTime.parse('18:00')],
    ],
    intervals: [ScheduleTime.parse('16:00')],
    triggers: [ScheduleTime.parse('14:27'), ScheduleTime.parse('15:35'), ScheduleTime.parse('17:38')],
  }
}

const scheduleToFullyRandomGraphData = ({
  schedule,
  simulation,
}: Pick<EmaExampleGraphProps, 'schedule' | 'simulation'>): FullyRandomGraphData => {
  if (schedule && simulation) {
    if (!isNil(schedule.startTime) && !isNil(schedule.endTime) && !isNil(schedule.emaMinimumGapSeconds)) {
      return {
        emaType: EmaType.Random,
        startTime: ScheduleTime.parse(schedule.startTime),
        endTime: ScheduleTime.parse(schedule.endTime),
        minimumInterval: schedule.emaMinimumGapSeconds,
        triggers: simulation.randomizedEmaTimeIntervals().map(([start]) => start),
      }
    }
  }
  // sample data
  return {
    emaType: EmaType.Random,
    startTime: ScheduleTime.parse('08:00'),
    endTime: ScheduleTime.parse('17:00'),
    minimumInterval: 3600,
    triggers: [
      ScheduleTime.parse('09:07'),
      ScheduleTime.parse('10:00'),
      ScheduleTime.parse('13:15'),
      ScheduleTime.parse('14:15'),
    ],
  }
}

const stringifyBaseGraphData = (data: BaseGraphData): BaseGraphDataStringRepresentation => {
  return {
    emaType: data.emaType,
    startTime: ScheduleTimeHelper.format(data.startTime.hours, data.startTime.minutes),
    endTime: ScheduleTimeHelper.format(data.endTime.hours, data.endTime.minutes),
    triggers: data.triggers.map((trigger: ScheduleTime) => ScheduleTimeHelper.format(trigger.hours, trigger.minutes)),
  }
}

const stringifySemiRandomGraphData = (data: SemiRandomGraphData): SemiRandomGraphDataStringRepresentation => {
  return {
    ...stringifyBaseGraphData(data),
    interval: ScheduleTimeHelper.getHoursAndMinutesUnitStringFromTotalSeconds(data.interval),
    flexibilityWindow: ScheduleTimeHelper.getHoursAndMinutesUnitStringFromTotalSeconds(data.flexibilityWindow),
  }
}

const stringifyFullyRandomGraphData = (data: FullyRandomGraphData): FullyRandomGraphDataStringRepresentation => {
  const ocurrences = data.triggers.length
  return {
    ...stringifyBaseGraphData(data),
    minimumInterval: ScheduleTimeHelper.getHoursAndMinutesUnitStringFromTotalSeconds(data.minimumInterval),
    ocurrences: `${ocurrences} time${ocurrences > 1 ? 's' : ''}`,
  }
}

// -----------------------------------------------------------------------------
// Graph Styles & Components
// -----------------------------------------------------------------------------
interface GraphContainerProps {
  renderTitle: () => ReactElement
  renderDescription: () => ReactElement
  renderContent: () => ReactElement
  onClickRefresh?: () => void
}

const GraphContainer = (props: GraphContainerProps) => {
  const {renderTitle, renderDescription, renderContent, onClickRefresh} = props

  const {color, fontWeight, fontSize} = selectTheme()

  const refreshButton = () => {
    return (
      <button
        type='button'
        css={{
          cursor: 'pointer',
          border: 'none',
          backgroundColor: 'transparent',
        }}
        onClick={() => onClickRefresh?.()}>
        <div
          css={{
            display: 'flex',
            justifyContent: 'flex-end',
            alignItems: 'center',
            gap: 2,
          }}>
          <RefreshIcon />
          <span
            css={{
              fontSize: fontSize.h7,
              fontWeight: fontWeight.thick,
              color: color.primary,
            }}>
            Refresh
          </span>
        </div>
      </button>
    )
  }

  return (
    <div
      css={{
        borderWidth: 1,
        borderRadius: 8,
        borderStyle: 'solid',
        borderColor: color.border.default,
        background: color.surface.white,
        display: 'flex',
        flexDirection: 'column',
        gap: 16,
        padding: '16px 0px',
      }}>
      <div css={{padding: '0px 16px', display: 'flex', flexDirection: 'column', gap: 16, width: '100%'}}>
        <div
          css={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            width: '100%',
          }}>
          {renderTitle()}
          {refreshButton()}
        </div>
        {renderDescription()}
      </div>
      <div
        css={{
          height: '1px',
          backgroundColor: color.border.default,
          width: '100%',
        }}
      />
      <div
        style={{
          overflow: 'auto hidden',
          height: 'fit-content',
        }}>
        {renderContent()}
      </div>
    </div>
  )
}

const GraphTitleStyle = (theme: Pick<ThemeState, 'fontSize' | 'fontWeight' | 'color'>): CSSProperties => {
  const {fontSize, fontWeight, color} = theme
  return {
    fontSize: fontSize.h6,
    fontWeight: fontWeight.thick,
    color: color.textIcon.title,
  }
}

const GraphDescriptionStyle = (theme: Pick<ThemeState, 'fontSize' | 'fontWeight' | 'color'>): CSSProperties => {
  const {fontSize, fontWeight, color} = theme
  return {
    fontSize: fontSize.h7,
    fontWeight: fontWeight.bold,
    color: color.textIcon.secondary,
  }
}

const GraphTextStyle = (theme: Pick<ThemeState, 'fontSize' | 'fontWeight'>): CSSProperties => {
  const {fontSize, fontWeight} = theme
  return {
    fontSize: fontSize.h7,
    fontWeight: fontWeight.thick,
    textAlign: 'center',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  }
}

const GraphTextCapsuleStyle = (theme: Pick<ThemeState, 'fontSize' | 'fontWeight'>): CSSProperties => {
  const {fontSize} = theme
  return {
    ...GraphTextStyle(theme),
    fontSize: fontSize.h7,
    borderRadius: 8,
    borderWidth: 1,
    borderStyle: 'solid',
    padding: '0px 6px',
  }
}

const GraphLargeTextCapsuleStyle = (theme: Pick<ThemeState, 'fontSize' | 'fontWeight'>): CSSProperties => {
  const {fontSize, fontWeight} = theme
  return {
    ...GraphTextCapsuleStyle(theme),
    fontSize: fontSize.h6,
    fontWeight: fontWeight.bold,
    padding: 4,
  }
}

const GraphLightPurpleTextCapsuleStyle = (
  theme: Pick<ThemeState, 'fontSize' | 'fontWeight' | 'color'>,
): CSSProperties => {
  const {color} = theme
  return {
    ...GraphTextCapsuleStyle(theme),
    backgroundColor: color.tag.purple.light,
    borderColor: color.tag.purple.dark,
    color: color.tag.purple.dark,
  }
}

const GraphDarkTextCapsuleStyle = (theme: Pick<ThemeState, 'fontSize' | 'fontWeight' | 'color'>): CSSProperties => {
  const {color} = theme
  return {
    ...GraphTextCapsuleStyle(theme),
    backgroundColor: color.surface.dark,
    borderColor: color.surface.dark,
    color: color.textIcon.white,
  }
}

const GraphLargeDarkTextCapsuleStyle = (
  theme: Pick<ThemeState, 'fontSize' | 'fontWeight' | 'color'>,
): CSSProperties => {
  return {
    ...GraphDarkTextCapsuleStyle(theme),
    ...GraphLargeTextCapsuleStyle(theme),
  }
}

const GraphPurpleTextCapsuleStyle = (theme: Pick<ThemeState, 'fontSize' | 'fontWeight' | 'color'>): CSSProperties => {
  const {color} = theme
  return {
    ...GraphTextCapsuleStyle(theme),
    backgroundColor: color.tag.purple.dark,
    borderColor: color.tag.purple.dark,
    color: color.textIcon.white,
  }
}

const GraphLargePurpleTextCapsuleStyle = (
  theme: Pick<ThemeState, 'fontSize' | 'fontWeight' | 'color'>,
): CSSProperties => {
  return {
    ...GraphPurpleTextCapsuleStyle(theme),
    ...GraphLargeTextCapsuleStyle(theme),
  }
}

const GraphBlueTextCapsuleStyle = (theme: Pick<ThemeState, 'fontSize' | 'fontWeight' | 'color'>): CSSProperties => {
  const {color} = theme
  return {
    ...GraphTextCapsuleStyle(theme),
    backgroundColor: color.tag.blue.dark,
    borderColor: color.tag.blue.dark,
    color: color.textIcon.white,
  }
}

const GraphLightBlueTextCapsuleStyle = (
  theme: Pick<ThemeState, 'fontSize' | 'fontWeight' | 'color'>,
): CSSProperties => {
  const {color} = theme
  return {
    ...GraphTextCapsuleStyle(theme),
    backgroundColor: color.tag.blue.light,
    borderColor: color.tag.blue.dark,
    color: color.tag.blue.dark,
  }
}

const GraphYellowTextCapsuleStyle = (theme: Pick<ThemeState, 'fontSize' | 'fontWeight' | 'color'>): CSSProperties => {
  const {color} = theme
  return {
    ...GraphTextCapsuleStyle(theme),
    backgroundColor: color.tag.yellow.dark,
    borderColor: color.tag.yellow.dark,
    color: color.textIcon.white,
  }
}

const GraphLargeYellowTextCapsuleStyle = (
  theme: Pick<ThemeState, 'fontSize' | 'fontWeight' | 'color'>,
): CSSProperties => {
  return {
    ...GraphYellowTextCapsuleStyle(theme),
    ...GraphLargeTextCapsuleStyle(theme),
  }
}

const GraphLegendStyle = (theme: Pick<ThemeState, 'fontSize' | 'fontWeight' | 'color'>): CSSProperties => {
  const {fontSize, color} = theme
  return {
    ...GraphTextStyle(theme),
    fontSize: fontSize.h8,
    color: color.textIcon.title,
  }
}

interface TimelineCapsuleProps<K extends Key> {
  index: K
  time: ScheduleTime
  legend?: string
  timeStyle?: CSSProperties
  legendStyle?: CSSProperties
  style?: CSSProperties
  onRelativePositionChange?: (position: Position, index: K) => void
}

const TimelineCapsule = <K extends Key = number>(props: TimelineCapsuleProps<K>) => {
  const {index, time, legend, timeStyle, legendStyle, onRelativePositionChange, style} = props

  const theme = selectTheme()

  const capsuleRef = useRef<HTMLDivElement>(null)

  useLayoutEffect(() => {
    const capsule = capsuleRef.current
    if (capsule) onRelativePositionChange?.(getRelativePosition(capsule), index)
  }, [capsuleRef, props])

  return (
    <div
      ref={capsuleRef}
      css={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: 4,
        ...style,
      }}>
      <div
        css={{
          ...GraphLargeTextCapsuleStyle(theme),
          minWidth: DEFAULT_CAPSULE_WIDTH,
          maxWidth: 'fit-content',
          minHeight: DEFAULT_CAPSULE_HEIGHT,
          maxHeight: 'fit-content',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          ...timeStyle,
        }}>
        {ScheduleTimeHelper.format(time.hours, time.minutes)}
      </div>
      {RIF(
        !isNil(legend),
        <div css={{...GraphLegendStyle(theme), width: 'fit-content', height: 'fit-content', ...legendStyle}}>
          {legend}
        </div>,
      )}
    </div>
  )
}

interface TriggerItemProps {
  index: number
  trigger: ScheduleTime
  style?: CSSProperties
  onRelativePositionChange?: (position: Position, index: number) => void
}

const TriggerItem = (props: TriggerItemProps) => {
  const {index, trigger, style, onRelativePositionChange} = props

  const theme = selectTheme()
  const {color} = theme

  const itemRef = useRef<HTMLDivElement>(null)

  useLayoutEffect(() => {
    const item = itemRef.current
    if (item) onRelativePositionChange?.(getRelativePosition(item), index)
  }, [itemRef, props])

  const ordinalIndexString = formatOrdinals(index + 1)

  return (
    <div
      ref={itemRef}
      css={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: 2,
        ...style,
      }}>
      <PinIcon width={22} height={48} />
      <TimelineCapsule
        {...{
          index,
          time: trigger,
          legend: `${ordinalIndexString} Trigger`,
          timeStyle: GraphLargeYellowTextCapsuleStyle(theme),
          legendStyle: {
            color: color.textIcon.attention,
          },
        }}
      />
    </div>
  )
}

interface IntervalItemProps<K extends Key> {
  index: K
  interval: [ScheduleTime, ScheduleTime]
  flexibilityWindows: [ScheduleTime, ScheduleTime][]
  widthMultiplier: number
  style?: CSSProperties
  onRelativePositionChange?: (position: Position, index: K) => void
}

const IntervalItem = <K extends Key = number>(props: IntervalItemProps<K>) => {
  const {index, interval, flexibilityWindows, widthMultiplier, style, onRelativePositionChange} = props

  const theme = selectTheme()
  const {color, fontSize} = theme

  const totalSeconds = ScheduleTime.getSecondsInterval(...interval)
  const intervalWidth = calculateWidth({totalSeconds, widthMultiplier})

  const intervalRef = useRef<HTMLDivElement>(null)
  const windowRef = useRef<HTMLDivElement>(null)

  useLayoutEffect(() => {
    const interval = intervalRef.current
    if (interval) onRelativePositionChange?.(getRelativePosition(interval), index)
  }, [intervalRef, props])

  const flexibilityWindowItem = (flexibilityWindow: [ScheduleTime, ScheduleTime], sign: '+' | '-') => {
    const totalSeconds = ScheduleTime.getSecondsInterval(...flexibilityWindow)
    const windowWidth = calculateWidth({totalSeconds, widthMultiplier})
    return (
      <div
        css={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'flex-end',
          gap: 4,
          width: windowWidth,
          height: 'fit-content',
        }}>
        <p
          css={{
            ...GraphTextStyle(theme),
            fontSize: fontSize.h8,
            color: color.tag.blue.dark,
            maxWidth: intervalWidth / 2,
          }}>
          Flexibility Window
        </p>
        <p
          css={{
            ...GraphBlueTextCapsuleStyle(theme),
            width: '100%',
          }}>{`${sign} ${ScheduleTimeHelper.getHoursAndMinutesUnitStringFromTotalSeconds(totalSeconds)}`}</p>
        <div
          css={{
            background: color.tag.blue.dark,
            opacity: 0.08,
            width: windowWidth,
            height: 74,
            marginTop: -8,
          }}
        />
      </div>
    )
  }

  return (
    <div
      ref={intervalRef}
      css={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'flex-end',
        height: windowRef.current?.clientHeight ?? 'fit-content',
        width: intervalWidth,
        position: 'relative',
        ...style,
      }}>
      {RIF(
        flexibilityWindows.length,
        <div
          ref={windowRef}
          css={{
            display: 'flex',
            justifyContent: 'space-between',
            width: '100%',
            height: 'fit-content',
            position: 'absolute',
            bottom: 0,
          }}>
          {flexibilityWindows.map((flexibilityWindow, index) => (
            <Fragment key={index}>{flexibilityWindowItem(flexibilityWindow, index % 2 === 0 ? '+' : '-')}</Fragment>
          ))}
        </div>,
      )}
      <p
        css={{
          ...GraphTextStyle(theme),
          fontSize: fontSize.h8,
          color: color.tag.purple.dark,
          maxWidth: intervalWidth,
          height: 'fit-content',
        }}>
        Approximate Interval
      </p>
      <div
        css={{
          ...GraphLightPurpleTextCapsuleStyle(theme),
          width: '100%',
          height: 'fit-content',
        }}>
        {ScheduleTimeHelper.getHoursAndMinutesUnitStringFromTotalSeconds(totalSeconds)}
      </div>
    </div>
  )
}

interface IntervalTrigger {
  time: ScheduleTime
  intervalIndex: number
  triggerIndex: number
}

const SemiRandomGraphContent = ({graphData}: {graphData: SemiRandomGraphData}) => {
  const theme = selectTheme()
  const {color} = theme

  const widthMultiplier = useMemo(
    () => calculateWidthModifier({startTime: graphData.startTime, endTime: graphData.endTime, minWidthMultiplier: 3}),
    [graphData],
  )

  const timeIntervals = Array.from(pairwise([graphData.startTime, ...graphData.intervals, graphData.endTime]))

  const triggers = useMemo<IntervalTrigger[]>(
    () =>
      Object.entries(
        groupBy(graphData.triggers, (trigger) =>
          timeIntervals.findIndex(
            ([start, end]) =>
              trigger.totalSeconds() >= start.totalSeconds() && trigger.totalSeconds() <= end.totalSeconds(),
          ),
        ),
      ).flatMap(([key, triggers]) => {
        const intervalIndex = parseInt(key)
        if (intervalIndex === -1) return []
        return triggers.map((trigger, triggerIndex) => ({
          time: trigger,
          intervalIndex,
          triggerIndex,
        }))
      }),
    [graphData],
  )

  const [startTimePosition, setStartTimePosition] = useState<Position | null>(null)
  const [endTimePosition, setEndTimePosition] = useState<Position | null>(null)
  const [intervalCapsulePositions, intervalCapsulePositionsActions] = useList<Position | null>(
    timeIntervals.map(() => null),
  )
  const [triggerCapsulePositions, triggerCapsulePositionsActions] = useList<Position | null>(
    graphData.triggers.map(() => null),
  )

  const edgeCapsule = (
    time: ScheduleTime,
    legend: string,
    onRelativePositionChange?: TimelineCapsuleProps<string>['onRelativePositionChange'],
  ) => {
    return (
      <TimelineCapsule
        {...{
          index: legend,
          time,
          legend,
          timeStyle: GraphLargeDarkTextCapsuleStyle(theme),
          legendStyle: {
            color: color.textIcon.title,
          },
          onRelativePositionChange,
        }}
      />
    )
  }

  const timeCapsule = (time: ScheduleTime, index: number) => {
    return (
      <TimelineCapsule
        {...{
          index,
          time,
          timeStyle: GraphLargePurpleTextCapsuleStyle(theme),
          legend: 'Interval',
          legendStyle: {
            color: color.transparent,
            userSelect: 'none',
            cursor: 'default',
          },
        }}
      />
    )
  }

  const intervals = () => {
    const startTimeLegend = 'Start Time'
    const endTimeLegend = 'End Time'
    return (
      <div
        css={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'flex-end',
          height: 'fit-content',
          width: 'fit-content',
          paddingBottom: 24, // account for trigger vertical offsets to avoid clipping
        }}>
        {edgeCapsule(graphData.startTime, startTimeLegend, (position) => {
          if (!isPositionEqual(startTimePosition, position)) {
            setStartTimePosition(position)
          }
        })}
        {timeIntervals.map((interval, index) => {
          const flexibilityWindows = graphData.flexibilityWindows.filter(
            ([windowStart, windowEnd]) =>
              windowStart.totalSeconds() >= interval[0].totalSeconds() &&
              windowEnd.totalSeconds() <= interval[1].totalSeconds(),
          )
          return (
            <Fragment key={index}>
              <div
                css={{
                  display: 'flex',
                  flexDirection: 'column',
                }}>
                <IntervalItem
                  {...{
                    index,
                    interval,
                    flexibilityWindows,
                    widthMultiplier,
                    style: {
                      marginBottom: 22,
                    },
                    onRelativePositionChange: (position, index) => {
                      if (!isPositionEqual(intervalCapsulePositions[index], position)) {
                        intervalCapsulePositionsActions.updateAt(index, position)
                      }
                    },
                  }}
                />
              </div>
              {RIF(graphData.intervals[index], (intervalTime) => timeCapsule(intervalTime, index))}
            </Fragment>
          )
        })}
        {edgeCapsule(graphData.endTime, endTimeLegend, (position) => {
          if (!isPositionEqual(endTimePosition, position)) {
            setEndTimePosition(position)
          }
        })}
      </div>
    )
  }

  const capsules = () => {
    return triggers.map(({time: trigger, intervalIndex}, index) => {
      const intervalPosition = intervalCapsulePositions[intervalIndex]

      const [start, end] = timeIntervals[intervalIndex]
      const totalSeconds = ScheduleTime.getSecondsInterval(start, end)
      const triggerSeconds = ScheduleTime.getSecondsInterval(start, trigger)

      const triggerPosition = triggerCapsulePositions[index]
      const left =
        (intervalPosition?.left ?? 0) +
        (intervalPosition?.width ?? 0) * (triggerSeconds / totalSeconds) -
        (triggerPosition?.width ?? 0) / 2

      return (
        <TriggerItem
          key={index}
          {...{
            index,
            trigger,
            style: {
              position: 'absolute',
              zIndex: 2,
              left,
              top: (startTimePosition?.top ?? 0) - 24,
            },
            onRelativePositionChange: (position, index) => {
              if (!isPositionEqual(triggerCapsulePositions[index], position)) {
                triggerCapsulePositionsActions.updateAt(index, position)
              }
            },
          }}
        />
      )
    })
  }

  return (
    <div
      css={{
        height: 'fit-content',
        width: 'fit-content',
        margin: 16,
        position: 'relative',
      }}>
      {intervals()}
      {capsules()}
    </div>
  )
}

const FullyRandomGraphContent = ({graphData}: {graphData: FullyRandomGraphData}) => {
  const theme = selectTheme()
  const {color} = theme

  const widthMultiplier = useMemo(
    () => calculateWidthModifier({startTime: graphData.startTime, endTime: graphData.endTime, minWidthMultiplier: 1}),
    [graphData],
  )

  const [startTimePosition, setStartTimePosition] = useState<Position | null>(null)
  const [endTimePosition, setEndTimePosition] = useState<Position | null>(null)
  const [triggerPositions, triggerPositionsActions] = useList<Position | null>(graphData.triggers.map(() => null))

  const timelineLeft = startTimePosition?.x ?? 0
  const timelineRight = endTimePosition?.x ?? 0
  const timelineWidth = timelineRight - timelineLeft

  const timelineHeight = 1
  const timelineTop = (startTimePosition?.top ?? 0) - timelineHeight - 8

  const edgeCapsule = (
    time: ScheduleTime,
    legend: string,
    onRelativePositionChange?: TimelineCapsuleProps<string>['onRelativePositionChange'],
  ) => {
    return (
      <TimelineCapsule
        {...{
          index: legend,
          time,
          legend,
          timeStyle: GraphLargeDarkTextCapsuleStyle(theme),
          legendStyle: {
            color: color.textIcon.title,
          },
          onRelativePositionChange,
        }}
      />
    )
  }

  const intervals = () => {
    if (graphData.triggers.length < 2) {
      return null
    }

    const elements: ReactElement[] = []
    for (const [triggerIndex, trigger] of graphData.triggers.entries()) {
      const nextTrigger = graphData.triggers[triggerIndex + 1]
      if (!nextTrigger) {
        continue
      }

      const interval = ScheduleTime.getSecondsInterval(trigger, nextTrigger)
      const marginWidth = 6
      const left = (triggerPositions[triggerIndex]?.x ?? 0) + marginWidth
      const width = (triggerPositions[triggerIndex + 1]?.x ?? 0) - left - marginWidth

      const element = (
        <div
          key={triggerIndex}
          css={{
            ...GraphLightBlueTextCapsuleStyle(theme),
            borderColor: color.tag.blue.light,
            position: 'absolute',
            left,
            width,
            top: timelineTop - 20,
          }}>
          {ScheduleTimeHelper.getHoursAndMinutesUnitStringFromTotalSeconds(interval)}
        </div>
      )

      elements.push(element)
    }

    return elements
  }

  const timeline = () => {
    const diameter = 8
    const circle = (left: number) => {
      return (
        <div
          css={{
            position: 'absolute',
            left,
            top: timelineTop - diameter / 2 + timelineHeight,
            width: diameter,
            height: diameter,
            borderRadius: '50%',
            backgroundColor: color.border.black,
          }}
        />
      )
    }

    return (
      <>
        {circle(timelineLeft - diameter / 2)}
        <div
          css={{
            height: timelineHeight,
            backgroundColor: color.border.black,
            position: 'absolute',
            left: timelineLeft,
            width: timelineWidth,
            top: timelineTop,
            zIndex: 1,
          }}
        />
        {circle(timelineRight - diameter / 2)}
      </>
    )
  }

  const capsules = () => {
    const startTimeLegend = 'Start Time'
    const endTimeLegend = 'End Time'
    return (
      <div
        css={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'flex-end',
          height: 'fit-content',
          width: 'fit-content',
        }}>
        {edgeCapsule(graphData.startTime, startTimeLegend, (position) => {
          if (!isPositionEqual(startTimePosition, position)) {
            setStartTimePosition(position)
          }
        })}
        {graphData.triggers.map((trigger, index, array) => {
          const style: CSSProperties = {
            zIndex: 2,
          }

          if (index === 0) {
            style.marginLeft = calculateWidth({startTime: graphData.startTime, endTime: trigger, widthMultiplier})
          } else {
            style.marginLeft = calculateWidth({startTime: array[index - 1], endTime: trigger, widthMultiplier})
          }
          if (index === array.length - 1) {
            style.marginRight = calculateWidth({startTime: trigger, endTime: graphData.endTime, widthMultiplier})
          }

          return (
            <TriggerItem
              key={index}
              {...{
                index,
                trigger,
                style,
                onRelativePositionChange: (position, index) => {
                  if (!isPositionEqual(triggerPositions[index], position)) {
                    triggerPositionsActions.updateAt(index, position)
                  }
                },
              }}
            />
          )
        })}
        {edgeCapsule(graphData.endTime, endTimeLegend, (position) => {
          if (!isPositionEqual(endTimePosition, position)) {
            setEndTimePosition(position)
          }
        })}
      </div>
    )
  }

  return (
    <div
      css={{
        height: 'fit-content',
        width: 'fit-content',
        margin: 16,
        position: 'relative',
      }}>
      {intervals()}
      {timeline()}
      {capsules()}
    </div>
  )
}

// -----------------------------------------------------------------------------
// Public Components
// -----------------------------------------------------------------------------
export const EmaExampleSemiRandomGraph = (props: EmaExampleGraphProps) => {
  const {onClickRefresh, ...dataArgs} = props
  const theme = selectTheme()
  const graphData = scheduleToSemiRandomGraphData(dataArgs)

  const renderTitle = () => {
    return <p css={{...GraphTitleStyle(theme)}}>EXAMPLE - SEMI RANDOMIZED</p>
  }

  const renderDescription = () => {
    const {interval, startTime, endTime, flexibilityWindow, triggers} = stringifySemiRandomGraphData(graphData)
    return (
      <div>
        <p css={{...GraphDescriptionStyle(theme)}}>
          Task will appear approximately every{' '}
          <span css={{...GraphLightPurpleTextCapsuleStyle(theme)}}>{interval}</span> between{' '}
          <span css={{...GraphDarkTextCapsuleStyle(theme)}}>{startTime}</span> to{' '}
          <span css={{...GraphDarkTextCapsuleStyle(theme)}}>{endTime}</span> , within a flexibility window of{' '}
          <span css={{...GraphBlueTextCapsuleStyle(theme)}}>{flexibilityWindow}</span>
        </p>
        {RIF(triggers.length, () => (
          <p css={{...GraphDescriptionStyle(theme), marginTop: '8px'}}>
            → One possible outcome could be the task appearing at
            {triggers.map((trigger, index) => (
              <Fragment key={index}>
                {' '}
                <span css={{...GraphYellowTextCapsuleStyle(theme)}}>{trigger}</span>
              </Fragment>
            ))}
          </p>
        ))}
      </div>
    )
  }

  const renderContent = () => {
    return <SemiRandomGraphContent {...{graphData}} />
  }

  return (
    <GraphContainer
      {...{
        renderTitle,
        renderDescription,
        renderContent,
        onClickRefresh,
      }}
    />
  )
}

export const EmaExampleFullyRandomGraph = (props: EmaExampleGraphProps) => {
  const {onClickRefresh, ...dataArgs} = props
  const theme = selectTheme()
  const graphData = scheduleToFullyRandomGraphData(dataArgs)

  const renderTitle = () => {
    return <p css={{...GraphTitleStyle(theme)}}>EXAMPLE - FULLY RANDOMIZED</p>
  }

  const renderDescription = () => {
    const {startTime, endTime, minimumInterval, ocurrences, triggers} = stringifyFullyRandomGraphData(graphData)
    return (
      <div>
        <p css={{...GraphDescriptionStyle(theme)}}>
          Task will appear randomly <span css={{...GraphYellowTextCapsuleStyle(theme)}}>{ocurrences}</span> between{' '}
          <span css={{...GraphDarkTextCapsuleStyle(theme)}}>{startTime}</span> to{' '}
          <span css={{...GraphDarkTextCapsuleStyle(theme)}}>{endTime}</span> , with minimum interval of{' '}
          <span css={{...GraphBlueTextCapsuleStyle(theme)}}>{minimumInterval}</span>
        </p>
        {RIF(triggers.length, () => (
          <p css={{...GraphDescriptionStyle(theme), marginTop: '8px'}}>
            → One possible outcome could be the task appearing at
            {triggers.map((trigger, index) => (
              <Fragment key={index}>
                {' '}
                <span css={{...GraphYellowTextCapsuleStyle(theme)}}>{trigger}</span>
              </Fragment>
            ))}
          </p>
        ))}
      </div>
    )
  }

  const renderContent = () => {
    return <FullyRandomGraphContent {...{graphData}} />
  }

  return (
    <GraphContainer
      {...{
        renderTitle,
        renderDescription,
        renderContent,
        onClickRefresh,
      }}
    />
  )
}
